// frontend/src/components/admin/SocialMediaConfig.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './SocialMediaConfig.css';

const ALL_CATEGORIES = ['crypto', 'ai', 'politics'];

const PLATFORMS = [
  {
    id: 'x',
    name: 'X (Twitter)',
    icon: '𝕏',
    fields: [
      { key: 'bearer_token',        label: 'Bearer Token',        secret: true,  placeholder: 'Bearer token from X Developer Portal' },
      { key: 'api_key',             label: 'API Key (Consumer)',  secret: true,  placeholder: 'Consumer Key' },
      { key: 'api_secret',          label: 'API Secret',          secret: true,  placeholder: 'Consumer Secret' },
      { key: 'access_token',        label: 'Access Token',        secret: true,  placeholder: 'OAuth 1.0a Access Token' },
      { key: 'access_token_secret', label: 'Access Token Secret', secret: true,  placeholder: 'OAuth 1.0a Access Token Secret' },
    ],
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: '📸',
    fields: [
      { key: 'meta_app_id',       label: 'Meta App ID',             secret: false, placeholder: 'From Meta Developer Dashboard' },
      { key: 'meta_app_secret',   label: 'Meta App Secret',         secret: true,  placeholder: 'App Secret' },
      { key: 'meta_page_id',      label: 'Facebook Page ID',        secret: false, placeholder: 'Linked Facebook Page ID' },
      { key: 'meta_access_token', label: 'Long-Lived Access Token', secret: true,  placeholder: 'Long-lived page token' },
    ],
  },
  {
    id: 'bluesky',
    name: 'Bluesky',
    icon: '🦋',
    fields: [
      { key: 'bluesky_handle',       label: 'Handle',       secret: false, placeholder: 'focusview.bsky.social' },
      { key: 'bluesky_app_password', label: 'App Password', secret: true,  placeholder: 'Generated in Bluesky Settings → App Passwords' },
    ],
  }
];

const DEFAULT_PLATFORM_STATE = {
  enabled: false,
  posting_enabled: false,
  bearer_token: '', api_key: '', api_secret: '', access_token: '', access_token_secret: '',
  meta_app_id: '', meta_app_secret: '', meta_page_id: '', meta_access_token: '',
  bluesky_handle: '', bluesky_app_password: '',
  categories: ['crypto', 'ai', 'politics'],
  configured: false,
};

const DEFAULT_MANUAL = { text: '', url: '', platforms: ['x'] };

const SocialMediaConfig = () => {
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [platforms, setPlatforms] = useState({
    x: { ...DEFAULT_PLATFORM_STATE },
    instagram: { ...DEFAULT_PLATFORM_STATE },
    bluesky: { ...DEFAULT_PLATFORM_STATE },
  });
  const [activePlatform, setActivePlatform] = useState('x');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState({});
  const [saveMsg, setSaveMsg] = useState('');
  const [error, setError] = useState('');

  // Daily digest state
  const [digestStatus, setDigestStatus] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [digestRunning, setDigestRunning] = useState(false);
  const [digestResult, setDigestResult] = useState(null);

  // Manual post state
  const [manual, setManual] = useState({ ...DEFAULT_MANUAL });
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/social-media/config', { headers: authHeader() });
      setMasterEnabled(res.data.master_enabled);
      setPlatforms(prev => {
        const merged = { ...prev };
        Object.keys(res.data.platforms).forEach(p => {
          const incoming = res.data.platforms[p];
          if (typeof incoming.categories === 'string') {
            try { incoming.categories = JSON.parse(incoming.categories); } catch { incoming.categories = ALL_CATEGORIES; }
          }
          merged[p] = { ...DEFAULT_PLATFORM_STATE, ...incoming };
        });
        return merged;
      });
    } catch {
      setError('Failed to load social media config');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDigestStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/social-media/daily-digest/status', { headers: authHeader() });
      setDigestStatus(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchConfig(); fetchDigestStatus(); }, [fetchConfig, fetchDigestStatus]);

  const handleMasterToggle = async (val) => {
    setMasterEnabled(val);
    try {
      await api.put('/admin/social-media/master-switch',
        { social_media_master_enabled: val },
        { headers: authHeader() }
      );
    } catch {
      setMasterEnabled(!val);
      setError('Failed to update master switch');
    }
  };

  const handlePlatformToggle = async (platform, field, val) => {
    setPlatforms(prev => ({ ...prev, [platform]: { ...prev[platform], [field]: val } }));
    if (field === 'posting_enabled') {
      try {
        await api.put(`/admin/social-media/toggle/${platform}`,
          { posting_enabled: val },
          { headers: authHeader() }
        );
      } catch {
        setPlatforms(prev => ({ ...prev, [platform]: { ...prev[platform], [field]: !val } }));
        setError('Failed to update platform toggle');
      }
    }
  };

  const handleFieldChange = (platform, field, val) =>
    setPlatforms(prev => ({ ...prev, [platform]: { ...prev[platform], [field]: val } }));

  const handleCategoryToggle = (platform, cat) => {
    setPlatforms(prev => {
      const current = prev[platform].categories || [];
      const updated = current.includes(cat) ? current.filter(c => c !== cat) : [...current, cat];
      if (updated.length === 0) return prev;
      return { ...prev, [platform]: { ...prev[platform], categories: updated } };
    });
  };

  const handleSave = async (platform) => {
    setSaving(true); setSaveMsg(''); setError('');
    try {
      const payload = {
        ...platforms[platform],
        categories: JSON.stringify(platforms[platform].categories || ALL_CATEGORIES),
      };
      await api.put(`/admin/social-media/config/${platform}`, payload, { headers: authHeader() });
      setSaveMsg(`✅ ${PLATFORMS.find(p => p.id === platform)?.name} config saved!`);
      setTimeout(() => setSaveMsg(''), 3000);
      fetchConfig();
    } catch {
      setError(`Failed to save ${platform} config`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (platform) => {
    setTestStatus(prev => ({ ...prev, [platform]: 'testing', [`${platform}_msg`]: '' }));
    try {
      const res = await api.post(`/admin/social-media/test/${platform}`, {}, { headers: authHeader() });
      setTestStatus(prev => ({
        ...prev,
        [platform]: res.data.success ? 'ok' : 'fail',
        [`${platform}_msg`]: res.data.message
      }));
    } catch {
      setTestStatus(prev => ({ ...prev, [platform]: 'fail', [`${platform}_msg`]: 'Connection test failed' }));
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true); setDigestPreview(null);
    try {
      const res = await api.get('/admin/social-media/daily-digest/preview', { headers: authHeader() });
      setDigestPreview(res.data);
    } catch (e) {
      setError('Preview failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRunDigest = async () => {
    if (!window.confirm('Post the daily digest to X now?')) return;
    setDigestRunning(true); setDigestResult(null); setError('');
    try {
      const res = await api.post('/admin/social-media/daily-digest/run', {}, { headers: authHeader() });
      setDigestResult(res.data);
      fetchDigestStatus();
    } catch (e) {
      setError('Digest post failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDigestRunning(false);
    }
  };

  const handleManualPost = async () => {
    if (!manual.platforms.length) { setError('Select at least one platform.'); return; }
    if (!manual.text) { setError('Enter post text.'); return; }
    setPosting(true); setPostResult(null); setError('');
    try {
      const res = await api.post('/admin/social-media/manual-post', {
        platforms: manual.platforms,
        text: manual.text,
        url: manual.url || null,
      }, { headers: authHeader() });
      setPostResult(res.data);
    } catch (e) {
      setError('Post failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return <div className="sm-loading"><div className="sm-spinner"></div><p>Loading...</p></div>;
  }

  const currentPlatform = PLATFORMS.find(p => p.id === activePlatform);
  const currentConfig = platforms[activePlatform];
  const activeCategories = currentConfig.categories || ALL_CATEGORIES;

  return (
    <div className="sm-config">

      {/* ── Header + Master Switch ── */}
      <div className="sm-header">
        <div>
          <h2>📣 Social Media</h2>
          <p className="sm-subtitle">Configure and post to X, Instagram, and Bluesky</p>
        </div>
        <div className="sm-master-switch">
          <span className="sm-master-label">All Social Posting</span>
          <button className={`sm-toggle-btn ${masterEnabled ? 'on' : 'off'}`} onClick={() => handleMasterToggle(!masterEnabled)}>
            <span className="sm-toggle-knob" />
          </button>
          <span className={`sm-master-status ${masterEnabled ? 'active' : 'inactive'}`}>
            {masterEnabled ? 'ENABLED' : 'PAUSED'}
          </span>
        </div>
      </div>

      {error   && <div className="sm-alert sm-alert-error">{error} <button onClick={() => setError('')}>×</button></div>}
      {saveMsg && <div className="sm-alert sm-alert-success">{saveMsg}</div>}
      {!masterEnabled && (
        <div className="sm-alert sm-alert-warn">⚠️ Master switch is off — no automated posts will be sent.</div>
      )}

      {/* ── Daily Digest Panel (X only) ── */}
      <div className="sm-panel sm-digest-panel">
        <div className="sm-panel-header">
          <h3>🗞️ X Daily Digest</h3>
          <span className="sm-manual-subtitle">One post per day • All categories • AI summaries • ~$0.20/day</span>
        </div>
        <div className="sm-panel-body">

          {/* Status row */}
          {digestStatus && (
            <div className="sm-digest-status-row">
              <div className="sm-digest-status-item">
                <span className="sm-digest-status-label">Today's status</span>
                <span className={`sm-digest-badge ${digestStatus.posted_today ? 'posted' : digestStatus.pending ? 'error' : 'pending'}`}>
                  {digestStatus.posted_today ? '✅ Posted' : digestStatus.pending ? '❌ Failed' : '⏳ Not posted yet'}
                </span>
              </div>
              {digestStatus.last_posted_date && (
                <div className="sm-digest-status-item">
                  <span className="sm-digest-status-label">Last posted</span>
                  <span className="sm-digest-status-val">{digestStatus.last_posted_date}</span>
                </div>
              )}
              {digestStatus.last_tweet_id && (
                <div className="sm-digest-status-item">
                  <span className="sm-digest-status-label">Tweet</span>
                  <a href={`https://x.com/i/web/status/${digestStatus.last_tweet_id}`} target="_blank" rel="noreferrer" className="sm-post-result-link">
                    View →
                  </a>
                </div>
              )}
              {digestStatus.pending && digestStatus.last_error && (
                <div className="sm-digest-error">⚠️ Last error: {digestStatus.last_error}</div>
              )}
            </div>
          )}

          <div className="sm-digest-schedule-note">
            🕘 Scheduled daily at <strong>9:00 AM ET</strong> — Posts automatically when X posting is enabled
          </div>

          {/* Actions */}
          <div className="sm-digest-actions">
            <button className="sm-preview-btn" onClick={handlePreview} disabled={previewLoading}>
              {previewLoading ? '⏳ Loading...' : '👁️ Preview Today\'s Post'}
            </button>
            <button className="sm-post-btn sm-post-btn-digest" onClick={handleRunDigest} disabled={digestRunning}>
              {digestRunning ? '⏳ Posting...' : '🚀 Post Now'}
            </button>
          </div>

          {/* Preview */}
          {digestPreview && (
            <div className="sm-digest-preview">
              <div className="sm-post-preview-label">Post preview (summaries will be AI-shortened in actual post):</div>
              <pre className="sm-digest-preview-text">{digestPreview.tweet_text}</pre>
              <div className="sm-digest-preview-meta">
                ~{digestPreview.char_count_estimate} chars (URLs count as 23 each)
              </div>
            </div>
          )}

          {/* Post result */}
          {digestResult && (
            <div className={`sm-post-result ${digestResult.success ? 'ok' : 'fail'}`}>
              <span className="sm-post-result-msg">
                {digestResult.success ? `✅ Posted to X! Tweet ID: ${digestResult.tweet_id}` : '❌ Post failed'}
              </span>
              {digestResult.tweet_url && (
                <a href={digestResult.tweet_url} target="_blank" rel="noreferrer" className="sm-post-result-link">View post →</a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Platform Cards ── */}
      <div className="sm-platform-cards">
        {PLATFORMS.map(p => {
          const cfg = platforms[p.id];
          const cats = cfg.categories || ALL_CATEGORIES;
          return (
            <div key={p.id} className={`sm-platform-card ${activePlatform === p.id ? 'active' : ''}`} onClick={() => setActivePlatform(p.id)}>
              <div className="sm-platform-card-top">
                <span className="sm-platform-icon">{p.icon}</span>
                <div className="sm-platform-info">
                  <div className="sm-platform-name-row">
                    <span className="sm-platform-name">{p.name}</span>
                    {cfg.configured && <span className="sm-platform-env-badge">✓ SET</span>}
                    {p.id !== 'x' && <span className="sm-platform-soon-badge">Soon</span>}
                  </div>
                  <span className={`sm-platform-badge ${cfg.posting_enabled && masterEnabled && p.id === 'x' ? 'live' : 'paused'}`}>
                    {cfg.posting_enabled && masterEnabled && p.id === 'x' ? '● Live' : '○ Paused'}
                  </span>
                </div>
              </div>
              {p.id === 'x' && (
                <div className="sm-platform-card-cats">
                  {ALL_CATEGORIES.map(c => (
                    <span key={c} className={`sm-cat-chip ${cats.includes(c) ? 'on' : 'off'}`}>{c}</span>
                  ))}
                </div>
              )}
              <div className="sm-platform-card-actions">
                {p.id === 'x' && (
                  <label className="sm-mini-toggle" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={cfg.posting_enabled}
                      onChange={e => handlePlatformToggle(p.id, 'posting_enabled', e.target.checked)} />
                    <span>Auto-posting</span>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Config Panel ── */}
      <div className="sm-panel">
        <div className="sm-panel-header">
          <h3>{currentPlatform.icon} {currentPlatform.name} Configuration</h3>
          <div className="sm-panel-actions">
            {activePlatform === 'x' && (
              <button
                className={`sm-test-btn ${testStatus[activePlatform] === 'ok' ? 'ok' : testStatus[activePlatform] === 'fail' ? 'fail' : ''}`}
                onClick={() => handleTest(activePlatform)}
                disabled={testStatus[activePlatform] === 'testing'}
              >
                {testStatus[activePlatform] === 'testing' ? '⏳ Testing...' :
                 testStatus[activePlatform] === 'ok'      ? '✅ Connected' :
                 testStatus[activePlatform] === 'fail'    ? '❌ Failed'    : '🔌 Test Connection'}
              </button>
            )}
            <button className="sm-save-btn" onClick={() => handleSave(activePlatform)} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Credentials'}
            </button>
          </div>
        </div>

        {testStatus[`${activePlatform}_msg`] && (
          <div className={`sm-test-msg ${testStatus[activePlatform] === 'ok' ? 'ok' : 'fail'}`}>
            {testStatus[`${activePlatform}_msg`]}
          </div>
        )}

        <div className="sm-panel-body">

          {/* Categories (X only) */}
          {activePlatform === 'x' && (
            <div className="sm-section">
              <h4>🗂️ Categories to Include in Daily Digest</h4>
              <p className="sm-section-desc">Select which news categories appear in today's digest post.</p>
              <div className="sm-cat-toggles">
                {ALL_CATEGORIES.map(cat => (
                  <button key={cat}
                    className={`sm-cat-toggle-btn ${activeCategories.includes(cat) ? 'active' : ''}`}
                    onClick={() => handleCategoryToggle(activePlatform, cat)} type="button">
                    {activeCategories.includes(cat) ? '✓' : '+'} {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Credentials */}
          <div className="sm-section">
            <h4>🔑 Credentials</h4>
            <p className="sm-section-desc">Stored securely in database. Paste new values to update; leave masked values unchanged to keep existing.</p>
            <div className="sm-fields">
              {currentPlatform.fields.map(f => (
                <div key={f.key} className="sm-field">
                  <label>{f.label}</label>
                  <input
                    type={f.secret ? 'password' : 'text'}
                    value={currentConfig[f.key] || ''}
                    onChange={e => handleFieldChange(activePlatform, f.key, e.target.value)}
                    placeholder={f.placeholder}
                    autoComplete="off"
                  />
                </div>
              ))}
            </div>
            {currentConfig.configured && (
              <p className="sm-creds-saved-note">✅ Credentials saved — masked values shown above. Paste new value to replace.</p>
            )}
            {activePlatform !== 'x' && (
              <p className="sm-creds-saved-note" style={{color:'#f59e0b'}}>⚡ {currentPlatform.name} posting coming soon — credentials stored for future use.</p>
            )}
          </div>

        </div>
      </div>

      {/* ── Manual Post Panel ── */}
      <div className="sm-panel sm-manual-panel">
        <div className="sm-panel-header">
          <h3>🚀 Manual Post</h3>
          <span className="sm-manual-subtitle">Post custom text to X immediately</span>
        </div>

        <div className="sm-panel-body">
          <div className="sm-section">
            <div className="sm-manual-fields">
              <div className="sm-field" style={{gridColumn:'1/-1'}}>
                <label>Post text</label>
                <textarea className="sm-template" rows={4}
                  value={manual.text}
                  onChange={e => setManual(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Type your post here..." />
                <span className="sm-char-count" style={{color: manual.text.length > 260 ? '#dc2626' : '#94a3b8'}}>
                  {manual.text.length}/280 chars {manual.text.length > 280 ? '(will be truncated)' : ''}
                </span>
              </div>
              <div className="sm-field" style={{gridColumn:'1/-1'}}>
                <label>URL to include (optional — adds $0.20 to post cost)</label>
                <input type="url" value={manual.url}
                  onChange={e => setManual(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://focusview.ai/article/..." />
              </div>
            </div>
          </div>

          <div className="sm-section">
            <button className="sm-post-btn" onClick={handleManualPost} disabled={posting}>
              {posting ? '⏳ Posting...' : '🚀 Post to X'}
            </button>
          </div>

          {postResult && (
            <div className="sm-section">
              {Object.entries(postResult.results).map(([platform, res]) => (
                <div key={platform} className={`sm-post-result ${res.success ? 'ok' : 'fail'}`}>
                  <span className="sm-post-result-msg">{res.message}</span>
                  {res.url && <a href={res.url} target="_blank" rel="noreferrer" className="sm-post-result-link">View post →</a>}
                </div>
              ))}
              {postResult.text_posted && (
                <div className="sm-post-preview">
                  <span className="sm-post-preview-label">Posted:</span>
                  <p className="sm-post-preview-text">{postResult.text_posted}</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

export default SocialMediaConfig;
